import * as React from 'react'
import { Loader2, LogOut } from 'lucide-react'
import { authenticationLogout, useCurrentUser } from '@axilon-apps/domains-identity/state'
import {
  createOntologyHeaders,
  getOntologyApiBaseUrl,
  isIdentityEnabled,
  useAppDispatch
} from '@axilon-apps/domains-ontology-ui-state'

import { Avatar, AvatarFallback } from '../../../components/ui/avatar'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { ConnectionCard } from './ConnectionCard'

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase()
}

function roleLabel(roleAssignments: Array<{ role?: { name?: string } }> | undefined): string {
  const first = roleAssignments?.[0]?.role?.name
  if (!first) return ''
  return first
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Compact account card for Settings → General (prototype tabs layout).
 * Identity: avatar / online / sign out. API-key mode: reuse ConnectionCard.
 */
export function AccountCard() {
  return isIdentityEnabled() ? <IdentityAccountCard /> : <ConnectionCard />
}

type SessionStatus = 'checking' | 'online' | 'offline'

function sessionBadge(status: SessionStatus): {
  label: string
  className: string
  dotClassName: string
} {
  switch (status) {
    case 'online':
      return {
        label: 'online',
        className: 'bg-st-run-bg text-st-run',
        dotClassName: 'bg-st-run'
      }
    case 'offline':
      return {
        label: 'offline',
        className: 'bg-st-down-bg text-st-down',
        dotClassName: 'bg-st-down'
      }
    default:
      return {
        label: 'checking…',
        className: 'bg-muted text-muted-foreground',
        dotClassName: 'bg-muted-foreground'
      }
  }
}

/**
 * Probes proxy `/health` with the signed-in JWT (`createOntologyHeaders`).
 * 200 → online; anything else (incl. network / 401) → offline.
 */
async function probeProxySession(signal?: AbortSignal): Promise<SessionStatus> {
  const endpoint = getOntologyApiBaseUrl()
  try {
    const response = await fetch(`${endpoint}/health`, {
      headers: createOntologyHeaders(),
      signal
    })
    return response.ok ? 'online' : 'offline'
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    return 'offline'
  }
}

function IdentityAccountCard() {
  const dispatch = useAppDispatch()
  const currentUser = useCurrentUser()
  const [sessionStatus, setSessionStatus] = React.useState<SessionStatus>('checking')

  React.useEffect(() => {
    const controller = new AbortController()
    void probeProxySession(controller.signal)
      .then(setSessionStatus)
      .catch((err: unknown) => {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          setSessionStatus('offline')
        }
      })
    return () => controller.abort()
  }, [])

  const name = typeof currentUser?.name === 'string' ? currentUser.name.trim() : ''
  const username = typeof currentUser?.username === 'string' ? currentUser.username.trim() : ''
  const displayName = name || username || 'Signed in'
  const role = roleLabel(currentUser?.roleAssignments)
  // Org has a human slug/name in identity, but the JWT only carries
  // opaque organization_id — don't surface that here.
  const subtitle = [username || null, role || null].filter(Boolean).join(' · ')
  const badge = sessionBadge(sessionStatus)

  const recheck = () => {
    setSessionStatus('checking')
    void probeProxySession().then(setSessionStatus)
  }

  return (
    <Card className="gap-0 overflow-hidden py-0 shadow-sm">
      <CardContent className="flex items-center justify-between gap-3 px-4 py-3.5">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar size="default" className="size-9 bg-brand/10 text-brand">
            <AvatarFallback className="bg-brand/10 text-xs font-semibold text-brand">
              {initialsFromName(displayName)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-ink">{displayName}</div>
            {subtitle ? <div className="truncate text-xs text-ink-3">{subtitle}</div> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="h-auto p-0 hover:bg-transparent"
            onClick={recheck}
            disabled={sessionStatus === 'checking'}
            aria-label="Recheck proxy session"
            title="Recheck proxy session"
          >
            <Badge variant="secondary" className={badge.className}>
              {sessionStatus === 'checking' ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <span className={`size-2 rounded-full ${badge.dotClassName}`} />
              )}
              {badge.label}
            </Badge>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => dispatch(authenticationLogout({}))}
          >
            <LogOut className="size-3.5" strokeWidth={1.75} />
            Sign out
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
